import { useState, useEffect, useCallback } from 'react';
import { posApi } from '../../../api/pos';

export enum PasswordType {
  Drawer = 1,
  Approval = 2,
}

interface SupervisorPinModalProps {
  title?: string;
  subtitle?: string;
  passwordType?: PasswordType;
  counterId?: number;
  onSuccess: () => void;
  onClose: () => void;
}

function getCounterIdFromStorage(): number {
  try {
    const raw = localStorage.getItem('counter');
    return raw ? (Number(JSON.parse(raw)?.id) || 0) : 0;
  } catch {
    return 0;
  }
}

export default function SupervisorPinModal({
  title = 'Supervisor Authorization',
  subtitle = 'Enter supervisor PIN to continue',
  passwordType = PasswordType.Approval,
  counterId,
  onSuccess,
  onClose,
}: SupervisorPinModalProps) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleVerify = useCallback(async (currentPin: string) => {
    const resolvedCounterId = counterId ?? getCounterIdFromStorage();
    setIsLoading(true);
    setError('');
    try {
      const res = await posApi.verifyPassword({
        counterId: resolvedCounterId,
        password: Number(currentPin),
        passwordType,
      });
      if (res.succeeded && res.data) {
        onSuccess();
      } else {
        setError(res.message || 'Invalid PIN');
        setPin('');
      }
    } catch {
      setError('Failed to verify PIN. Please try again.');
      setPin('');
    } finally {
      setIsLoading(false);
    }
  }, [passwordType, onSuccess]);

  const handlePinInput = (digit: string) => {
    if (!isLoading && pin.length < 6) { setPin(prev => prev + digit); setError(''); }
  };
  const handlePinDelete = () => { if (!isLoading) { setPin(prev => prev.slice(0, -1)); setError(''); } };
  const handlePinClear = () => { if (!isLoading) { setPin(''); setError(''); } };

  useEffect(() => {
    if (pin.length === 6) handleVerify(pin);
  }, [pin, handleVerify]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (isLoading) return;
    if (e.key >= '0' && e.key <= '9') handlePinInput(e.key);
    else if (e.key === 'Backspace') handlePinDelete();
    else if (e.key === 'Escape') onClose();
    else if (e.key === 'Enter' && pin.length === 6) handleVerify(pin);
  }, [pin, isLoading, handleVerify, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden">
        <div className="bg-orange-600 text-white px-5 py-4 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold whitespace-nowrap">{title}</h2>
            <p className="text-sm text-orange-100">{subtitle}</p>
          </div>
          <button onClick={onClose} disabled={isLoading} className="w-8 h-8 flex items-center justify-center hover:bg-orange-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-5">
          <label className="block text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">Enter Supervisor PIN</label>

          <div className="flex items-center justify-center gap-2 mb-3">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div key={index} className={`w-11 h-11 flex items-center justify-center border-2 rounded-lg text-xl font-bold transition-all ${pin.length > index ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-gray-300 bg-white text-gray-400'}`}>
                {pin.length > index ? '●' : '○'}
              </div>
            ))}
          </div>

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 mb-3">
              <i className="ri-error-warning-line text-red-600 text-xl"></i>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-3">
              <i className="ri-loader-4-line animate-spin"></i>
              Verifying...
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
              <button key={digit} onClick={() => handlePinInput(digit.toString())} disabled={isLoading} className="h-11 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xl font-bold transition-colors cursor-pointer disabled:opacity-50">{digit}</button>
            ))}
            <button onClick={handlePinClear} disabled={isLoading} className="h-11 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg text-sm font-bold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">Clear</button>
            <button onClick={() => handlePinInput('0')} disabled={isLoading} className="h-11 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xl font-bold transition-colors cursor-pointer disabled:opacity-50">0</button>
            <button onClick={handlePinDelete} disabled={isLoading} className="h-11 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-back-2-line text-xl"></i></button>
          </div>
        </div>
      </div>
    </div>
  );
}
